import { AxiosResponse } from "axios";
import { createApi } from "../../../../utils/createApi";
import { MAX_WAIT_MS, POLL_INTERVAL_MS } from "../vpnDebugConstants";
import { VpnDebugPollResponse, VpnDebugResult } from "../vpnDebugInterfaces";
import { useVpnDebuggerStore } from "../vpnDebuggerStore/vpnDebuggerStore";

/**
 * Poll GET /api/network-vpn-debug/{task_id}/ until the job resolves.
 *
 * Resolves with the result, or with `null` when a newer run has superseded this
 * one. Rejects on a failed job or on the wall-clock cap.
 *
 * Two behaviours are deliberate:
 *
 *   1. Transient network errors do NOT end the poll. The job is still running on
 *      the server and a dropped packet must not discard a two-minute run — which
 *      is also why this does not route through `universalAPIErrorHandler` the way
 *      the POSTs that start a run do.
 *   2. Every tick re-reads `runToken` from the store. Without it, starting a
 *      second run lets the first one's late response overwrite the new ladder.
 */
export const pollVpnDebugTask = (
  taskId: string,
  token: number,
  startedAt: number,
  label: string
): Promise<VpnDebugResult | null> =>
  new Promise((resolve, reject) => {
    const tick = () => {
      if (token !== useVpnDebuggerStore.getState().runToken) {
        resolve(null);
        return;
      }
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        reject(new Error(`${label} timed out after 10 minutes.`));
        return;
      }

      const AxiosUI = createApi("");
      AxiosUI.get(`network-vpn-debug/${taskId}/`).then(
        (response: AxiosResponse<VpnDebugPollResponse>) => {
          if (token !== useVpnDebuggerStore.getState().runToken) {
            resolve(null);
            return;
          }
          const data = response.data || ({} as VpnDebugPollResponse);

          if (data.status === "success") {
            resolve(data.result ?? null);
            return;
          }
          if (data.status === "failed") {
            reject(new Error(data.detail || data.error || `${label} failed.`));
            return;
          }
          // pending (or an unknown task id, which the server also reports as
          // pending because an enqueued-but-unclaimed job has no row yet)
          setTimeout(tick, POLL_INTERVAL_MS);
        },
        () => {
          setTimeout(tick, POLL_INTERVAL_MS);
        }
      );
    };

    tick();
  });
